import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import { TravelMap } from "../components/TravelMap";
import { 
  User, Mail, Phone, MapPin, Calendar, Clock, FileText, MessageSquare, 
  Euro, Briefcase, Users, CalendarDays, Repeat, Edit, ExternalLink, Save, X, Video, Send, Copy,
  Hash, Building2, ClipboardCheck, UserPlus, Shuffle, Sparkles, XCircle, CheckCircle2, Car
} from 'lucide-react';
import { companyApi } from '../lib/api';
import { toast } from 'sonner';

// Mapping des statuts en français (unifié : confirmed→Approuvé, cancelled→Refusé)
const STATUS_LABELS = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
  approved: { label: 'Approuvé', color: 'bg-green-100 text-green-800' },
  confirmed: { label: 'Approuvé', color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Refusé', color: 'bg-red-100 text-red-800' },
  cancelled: { label: 'Refusé', color: 'bg-red-100 text-red-800' },
  completed: { label: 'Terminé', color: 'bg-blue-100 text-blue-800' },
  advice_requested: { label: 'Demande conseil', color: 'bg-purple-100 text-purple-800' },
  awaiting_dates: { label: 'En attente dates', color: 'bg-orange-100 text-orange-800' },
  awaiting_client_validation: { label: 'Validation client', color: 'bg-indigo-100 text-indigo-800' },
};

// Mapping des fréquences
const FREQUENCY_LABELS = {
  once: 'Une seule fois',
  ponctuel: 'Ponctuel',
  weekly: 'Chaque semaine',
  biweekly: 'Toutes les 2 semaines',
  monthly: '1 fois par mois',
  custom: 'Personnalisé',
  ask_advice: 'Demande de conseil',
};

// Mapping des types de slot
const SLOT_TYPE_LABELS = {
  half_day: 'Demi-journée (3h30)',
  morning: 'Matin (3h30)',
  afternoon: 'Après-midi (3h30)',
  full_day: 'Journée complète (7h)',
  'journee_à_2_intervenants': 'Journée à 2 intervenants',
  journee_a_2_intervenants: 'Journée à 2 intervenants',
  'journee_à_1_intervenant': 'Journée à 1 intervenant',
  journee_a_1_intervenant: 'Journée à 1 intervenant',
  'demi_journee_à_2_intervenants': 'Demi-journée à 2 intervenants',
};

// Fallback: transform any raw slug (e.g. "journee_à_2_intervenants") into a
// readable French label for values that aren't in SLOT_TYPE_LABELS.
const humanizeSlot = (raw) => {
  if (!raw) return '—';
  if (SLOT_TYPE_LABELS[raw]) return SLOT_TYPE_LABELS[raw];
  return String(raw)
    .replace(/_/g, ' ')
    .replace(/^\w/, c => c.toUpperCase())
    .trim();
};

// Mapping des jours préférés
const DAY_LABELS = {
  monday: 'Lundi',
  tuesday: 'Mardi',
  wednesday: 'Mercredi',
  thursday: 'Jeudi',
  friday: 'Vendredi',
  saturday: 'Samedi',
  sunday: 'Dimanche',
  flexible: 'Flexible',
};

// Mapping des périodes préférées
const PERIOD_LABELS = {
  morning: 'Matin',
  afternoon: 'Après-midi',
  flexible: 'Flexible',
};

// Composant pour afficher un champ d'information avec lien optionnel
const InfoField = ({ icon: Icon, label, value, className = '', onClick, isLink = false }) => {
  if (!value && value !== 0) return null;
  return (
    <div className={`flex items-start gap-3 ${className}`}>
      {Icon && (
        <div className="w-8 h-8 rounded-lg bg-gray-100/80 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Icon className="w-3.5 h-3.5 text-gray-500" strokeWidth={2.2} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <span className="text-[10px] text-gray-400 uppercase tracking-[0.14em] font-bold block leading-none mb-1.5">{label}</span>
        {isLink ? (
          <button
            onClick={onClick}
            className="text-[15px] text-primary hover:text-primary/80 hover:underline underline-offset-2 font-semibold flex items-center gap-1.5 transition-colors"
          >
            {value}
            <ExternalLink className="w-3 h-3" />
          </button>
        ) : (
          <span className="text-[14.5px] text-gray-900 break-words font-medium leading-snug">{value}</span>
        )}
      </div>
    </div>
  );
};

// Composant pour afficher une section (carte moderne avec respiration)
const Section = ({ title, children, action, icon: SectionIcon, className = '', tone = 'default' }) => {
  const validChildren = React.Children.toArray(children).filter(child => child);
  if (validChildren.length === 0) return null;

  const toneConfig = {
    default: { bg: 'bg-white', border: 'border-gray-200/70', iconBg: 'bg-gray-100', iconColor: 'text-gray-600' },
    primary: { bg: 'bg-emerald-50/40', border: 'border-emerald-200/60', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-700' },
    purple: { bg: 'bg-purple-50/50', border: 'border-purple-200/70', iconBg: 'bg-purple-100', iconColor: 'text-purple-700' },
    blue: { bg: 'bg-blue-50/40', border: 'border-blue-200/70', iconBg: 'bg-blue-100', iconColor: 'text-blue-700' },
  };
  const cfg = toneConfig[tone] || toneConfig.default;

  return (
    <div className={`rounded-2xl border ${cfg.bg} ${cfg.border} p-5 sm:p-6 ${className} min-w-0 transition-shadow hover:shadow-sm`}>
      <div className="flex items-center justify-between mb-5 gap-2 flex-wrap">
        <h4 className="text-[11px] font-bold text-gray-700 uppercase tracking-[0.14em] flex items-center gap-2.5 min-w-0 flex-shrink">
          {SectionIcon && (
            <span className={`w-7 h-7 rounded-lg ${cfg.iconBg} flex items-center justify-center flex-shrink-0`}>
              <SectionIcon className={`w-3.5 h-3.5 ${cfg.iconColor}`} strokeWidth={2.4} />
            </span>
          )}
          <span className="truncate">{title}</span>
        </h4>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
};

// Composant pour afficher les réponses aux questions personnalisées
const CustomAnswers = ({ answers, questions }) => {
  if (!answers || Object.keys(answers).length === 0) return null;
  
  return (
    <Section title="Réponses aux questions">
      {Object.entries(answers).map(([key, value]) => {
        const question = questions?.find(q => q.id === key || q.label === key);
        const label = question?.label || key;
        
        let displayValue = value;
        if (Array.isArray(value)) {
          displayValue = value.join(', ');
        } else if (typeof value === 'boolean') {
          displayValue = value ? 'Oui' : 'Non';
        } else if (typeof value === 'object') {
          displayValue = JSON.stringify(value);
        }
        
        return (
          <InfoField 
            key={key} 
            icon={MessageSquare} 
            label={label} 
            value={displayValue} 
          />
        );
      })}
    </Section>
  );
};

export default function RequestDetailModal({ 
  open, 
  onOpenChange, 
  request, 
  type = 'subscription',
  questions = [],
  onRequestUpdated
}) {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editForm, setEditForm] = useState({});
  // Video meeting link state (for visio appointments)
  const [meetingLink, setMeetingLink] = useState('');
  const [isSavingLink, setIsSavingLink] = useState(false);
  // Collaborator assignment (when client said "Peu importe")
  const [suggestedCollabs, setSuggestedCollabs] = useState([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [isActing, setIsActing] = useState(false);
  // All company collaborators — used to resolve names/avatars from the IDs
  // stored on requests (preferred_collaborator_id, assigned_collaborator_id, …)
  const [allCollaborators, setAllCollaborators] = useState([]);

  // Fetch the full collaborators list once the modal is opened. We keep it
  // local to the modal to avoid touching all parent pages.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await companyApi.getCollaborators();
        if (!cancelled) setAllCollaborators(res.data || []);
      } catch {
        /* silent — falls back to raw ID display */
      }
    })();
    return () => { cancelled = true; };
  }, [open]);
  
  // Reset edit mode when modal closes or request changes
  useEffect(() => {
    if (!open) {
      setIsEditing(false);
    }
    if (request) {
      setEditForm({
        client_name: request.client_name || '',
        client_email: request.client_email || '',
        client_phone: request.client_phone || '',
        client_address: request.client_address || '',
        notes: request.notes || '',
        date: request.date || '',
        start_time: request.start_time || request.time_slot || '',
        status: request.status || 'pending',
      });
      setMeetingLink(request.meeting_link || '');
    }
  }, [open, request]);

  // Handlers for meeting link (video appointments)
  const isVideoAppointment = type === 'appointment' && (request?.meeting_type === 'online' || request?.meeting_type === 'video');
  const handleSaveMeetingLink = async (sendToClient) => {
    if (!request?.id) return;
    if (sendToClient && !meetingLink.trim()) {
      toast.error('Veuillez d\'abord saisir le lien de visioconférence');
      return;
    }
    setIsSavingLink(true);
    try {
      const res = await companyApi.updateAppointmentMeetingLink(request.id, {
        meeting_link: meetingLink.trim(),
        send_to_client: sendToClient,
      });
      if (sendToClient && res.data?.sent_to_client) {
        toast.success('Lien envoyé au client par email');
      } else {
        toast.success('Lien enregistré');
      }
      onRequestUpdated && onRequestUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setIsSavingLink(false);
    }
  };
  const handleCopyMeetingLink = async () => {
    if (!meetingLink) return;
    try {
      await navigator.clipboard.writeText(meetingLink);
      toast.success('Lien copié');
    } catch {
      toast.error('Impossible de copier le lien');
    }
  };

  // Collaborator assignment handlers
  const assignedCollabId = request?.assigned_collaborator_id || request?.collaborator_id || null;
  const preferredCollabId = request?.preferred_collaborator_id || null;
  // Le client "a coché Peu importe" quand aucun collaborateur (ni préféré, ni affecté)
  // n'est associé à la demande.
  const hasAssignedCollab = !!assignedCollabId;
  const hasPreferredOnly = !hasAssignedCollab && !!preferredCollabId;

  // Résout un collaborateur (nom + avatar + couleur) depuis un ID en s'appuyant
  // sur allCollaborators. Fallback sur les champs stockés côté request si présents.
  const resolveCollab = (collabId, fallbackName) => {
    if (!collabId) return null;
    const found = allCollaborators.find(c => c.id === collabId);
    return {
      id: collabId,
      name: found?.name || fallbackName || 'Collaborateur',
      avatar_url: found?.avatar_url || null,
      color: found?.color || null,
    };
  };

  const loadSuggestions = async () => {
    if (!request?.id) return;
    try {
      const res = await companyApi.getSuggestedCollaborators(request.id);
      setSuggestedCollabs(res.data || []);
      setShowAssignDialog(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Impossible de charger les suggestions');
    }
  };

  const handleAssign = async (collabIdOrIds) => {
    if (!request?.id) return;
    setIsAssigning(true);
    try {
      const isArr = Array.isArray(collabIdOrIds);
      const payload = isArr
        ? { collaborator_ids: collabIdOrIds.filter(Boolean) }
        : collabIdOrIds;
      const res = await companyApi.assignRequestCollaborator(request.id, payload);
      toast.success(res.data?.message || 'Collaborateur affecté');
      setShowAssignDialog(false);
      onRequestUpdated && onRequestUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setIsAssigning(false);
    }
  };

  // How many intervenants the slot requires (default 1). Derived from the
  // slot_type slug: "journee_à_2_intervenants" → 2, "demi_journee_à_2_intervenants" → 2.
  const requiredCollabCount = (() => {
    const slot = String(request?.slot_type || '');
    const m = slot.match(/(\d+)_intervenants?/i);
    if (m) return Math.max(1, parseInt(m[1], 10));
    return 1;
  })();

  if (!request) return null;
  
  const status = STATUS_LABELS[request.status] || { label: request.status, color: 'bg-gray-100 text-gray-800' };
  
  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };
  
  // Format datetime
  const formatDateTime = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Navigate to client page (existing client)
  const goToClient = async () => {
    try {
      const response = await companyApi.getClients();
      const clients = response.data || [];
      const client = clients.find(c =>
        c.email?.toLowerCase() === request.client_email?.toLowerCase()
      );

      if (client) {
        onOpenChange(false);
        navigate(`/company/clients/${client.id}`);
      } else {
        toast.info('Aucun client trouvé avec cet email. Utilisez "Créer fiche" pour en créer un.');
      }
    } catch (error) {
      console.error('Error finding client:', error);
      toast.error('Erreur lors de la recherche du client');
    }
  };

  // Create a new client from the request information
  const createClientFromRequest = async () => {
    if (!request?.client_email) {
      toast.error('Email du client manquant');
      return;
    }
    try {
      // First check if a client with this email already exists (avoid duplicates)
      const response = await companyApi.getClients();
      const clients = response.data || [];
      const existing = clients.find(c =>
        c.email?.toLowerCase() === request.client_email?.toLowerCase()
      );
      if (existing) {
        toast.info('Ce client existe déjà — redirection vers sa fiche.');
        onOpenChange(false);
        navigate(`/company/clients/${existing.id}`);
        return;
      }
      const newClientData = {
        name: request.client_name,
        email: request.client_email,
        phone: request.client_phone || '',
        address: request.client_address || '',
        urssaf_email: request.urssaf_email || '',
      };
      const createRes = await companyApi.createClient(newClientData);
      if (createRes.data?.id) {
        toast.success('Fiche client créée avec succès');
        onOpenChange(false);
        navigate(`/company/clients/${createRes.data.id}`);
      }
    } catch (createError) {
      console.error('Error creating client:', createError);
      toast.error(createError.response?.data?.detail || 'Erreur lors de la création du client');
    }
  };

  // Save edited request
  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Call API to update request based on type
      if (type === 'subscription') {
        await companyApi.updateSubscriptionRequest(request.id, editForm);
      } else if (type === 'punctual') {
        await companyApi.updateBooking(request.id, editForm);
      } else if (type === 'appointment') {
        await companyApi.updateAppointment(request.id, editForm);
      }
      
      toast.success('Demande mise à jour');
      setIsEditing(false);
      if (onRequestUpdated) {
        onRequestUpdated();
      }
    } catch (error) {
      console.error('Error updating request:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setIsSaving(false);
    }
  };

  // Quick approve / reject actions (visible in non-edit mode for actionable statuses)
  const ACTIONABLE_STATUSES = ['pending', 'advice_requested', 'awaiting_dates', 'awaiting_client_validation'];
  const canApproveOrReject = request && ACTIONABLE_STATUSES.includes(request.status);

  const handleApprove = async () => {
    if (!request?.id || isActing) return;
    setIsActing(true);
    try {
      if (type === 'subscription') {
        await companyApi.approveSubscription(request.id);
      } else if (type === 'appointment') {
        await companyApi.approveAppointment(request.id);
      } else if (type === 'punctual') {
        await companyApi.confirmPunctualBooking(request.id);
      }
      toast.success('Demande approuvée');
      onOpenChange(false);
      if (onRequestUpdated) onRequestUpdated();
    } catch (error) {
      console.error('Approve error:', error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'approbation");
    } finally {
      setIsActing(false);
    }
  };

  const handleReject = async () => {
    if (!request?.id || isActing) return;
    setIsActing(true);
    try {
      if (type === 'subscription') {
        await companyApi.rejectSubscription(request.id, 'Demande refusée');
      } else if (type === 'appointment') {
        await companyApi.rejectAppointment(request.id, 'Demande refusée');
      } else if (type === 'punctual') {
        await companyApi.cancelBooking(request.id);
      }
      toast.success('Demande refusée');
      onOpenChange(false);
      if (onRequestUpdated) onRequestUpdated();
    } catch (error) {
      console.error('Reject error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du refus');
    } finally {
      setIsActing(false);
    }
  };

  // Render edit form
  const renderEditForm = () => (
    <div className="space-y-4 py-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Nom du client</Label>
          <Input
            value={editForm.client_name}
            onChange={(e) => setEditForm(prev => ({ ...prev, client_name: e.target.value }))}
          />
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input
            type="email"
            value={editForm.client_email}
            onChange={(e) => setEditForm(prev => ({ ...prev, client_email: e.target.value }))}
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Téléphone</Label>
          <Input
            value={editForm.client_phone}
            onChange={(e) => setEditForm(prev => ({ ...prev, client_phone: e.target.value }))}
          />
        </div>
        <div className="space-y-2">
          <Label>Statut</Label>
          <Select 
            value={editForm.status} 
            onValueChange={(v) => setEditForm(prev => ({ ...prev, status: v }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">En attente</SelectItem>
              <SelectItem value="approved">Approuvé</SelectItem>
              <SelectItem value="completed">Terminé</SelectItem>
              <SelectItem value="rejected">Refusé</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Adresse</Label>
        <AddressAutocomplete
          value={editForm.client_address}
          onChange={(v) => setEditForm(prev => ({ ...prev, client_address: v }))}
          placeholder="Commencez à taper l'adresse…"
          data-testid="edit-address-autocomplete"
        />
      </div>
      
      {(type === 'punctual' || type === 'appointment') && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Date</Label>
            <Input
              type="date"
              value={editForm.date}
              onChange={(e) => setEditForm(prev => ({ ...prev, date: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label>Heure</Label>
            <Input
              type="time"
              value={editForm.start_time}
              onChange={(e) => setEditForm(prev => ({ ...prev, start_time: e.target.value }))}
            />
          </div>
        </div>
      )}
      
      <div className="space-y-2">
        <Label>Notes internes</Label>
        <Textarea
          value={editForm.notes}
          onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
          placeholder="Notes visibles uniquement par l'entreprise..."
          rows={3}
        />
      </div>
    </div>
  );

  // Render view mode
  const renderViewMode = () => (
    <div className="space-y-5 px-1">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Section Client */}
        <Section
          title="Informations client"
          icon={User}
          action={
            <div className="flex items-center gap-1.5">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs hover:bg-white"
                onClick={goToClient}
                data-testid="modal-client-link"
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                Fiche client
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs bg-white"
                onClick={createClientFromRequest}
                data-testid="modal-create-client-btn"
              >
                <UserPlus className="w-3 h-3 mr-1" />
                Créer fiche
              </Button>
            </div>
          }
        >
          <InfoField
            icon={User}
            label="Nom"
            value={request.client_name}
            isLink={true}
            onClick={goToClient}
          />
          <InfoField icon={Mail} label="Email" value={request.client_email} />
          <InfoField icon={Phone} label="Téléphone" value={request.client_phone} />
          <InfoField icon={MapPin} label="Adresse" value={request.client_address} />
          {request.urssaf_email && (
            <InfoField icon={Mail} label="Email URSSAF" value={request.urssaf_email} />
          )}
        </Section>

        {/* Section Prestation - pour tous les types */}
        <Section title="Prestation" icon={Briefcase}>
          {/* Pour les réservations ponctuelles et RDV */}
          {(type === 'punctual' || type === 'appointment') && (
            <>
              <InfoField icon={Briefcase} label="Service" value={request.service_name} />
              <InfoField icon={Calendar} label="Date" value={formatDate(request.date)} />
              <InfoField icon={Clock} label="Heure" value={request.start_time || request.time_slot} />
              {request.end_time && (
                <InfoField icon={Clock} label="Fin" value={request.end_time} />
              )}
              {request.duration_minutes && (
                <InfoField icon={Clock} label="Durée" value={`${request.duration_minutes} minutes`} />
              )}
              {request.duration && !request.duration_minutes && (
                <InfoField icon={Clock} label="Durée" value={`${request.duration} minutes`} />
              )}
              {request.slot_period && (
                <InfoField icon={Clock} label="Période" value={request.slot_period === 'morning' ? 'Matin' : 'Après-midi'} />
              )}
              {request.meeting_type && (
                <InfoField
                  icon={Users}
                  label="Type de rendez-vous"
                  value={(request.meeting_type === 'online' || request.meeting_type === 'video') ? 'Visioconférence' : 'En présentiel'}
                />
              )}
            </>
          )}

          {/* Pour les abonnements */}
          {type === 'subscription' && (
            <>
              {request.selected_service_names?.length > 0 && (
                <InfoField 
                  icon={Briefcase} 
                  label="Services" 
                  value={request.selected_service_names.join(', ')} 
                />
              )}
              {request.other_service_description && (
                <InfoField 
                  icon={Briefcase} 
                  label="Autre service" 
                  value={request.other_service_description} 
                />
              )}
              <InfoField 
                icon={Repeat} 
                label="Fréquence" 
                value={FREQUENCY_LABELS[request.frequency] || humanizeSlot(request.frequency)} 
              />
              <InfoField 
                icon={Clock} 
                label="Type d'intervention" 
                value={humanizeSlot(request.slot_type)} 
              />
              {request.passages_per_year > 0 && (
                <InfoField 
                  icon={CalendarDays} 
                  label="Passages/période" 
                  value={(() => {
                    // Une journée complète = 1 passage (matin+aprem = même passage).
                    // Le backend stocke parfois 2 (matin + aprem comptés séparément)
                    // pour les full_day → on divise pour l'affichage.
                    const raw = request.passages_per_year;
                    const slot = String(request.slot_type || '');
                    const isFullDay = slot.includes('journee') || slot.includes('full_day') || slot.includes('journée');
                    const display = isFullDay && raw % 2 === 0 ? raw / 2 : raw;
                    return `${display} passage${display > 1 ? 's' : ''}`;
                  })()} 
                />
              )}
            </>
          )}
        </Section>
      </div>

      {/* Section spéciale Lien Google Meet pour les RDV en visio (full width) */}
      {isVideoAppointment && (
        <Section title="Lien de visioconférence" icon={Video} tone="purple">
          <div className="space-y-3 -mt-1">
            <p className="text-xs text-purple-700">
              Collez ici votre lien Google Meet, Zoom, Teams, Whereby... Le client recevra le lien par email lorsque vous l'enverrez.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Video className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="url"
                  data-testid="meeting-link-input"
                  placeholder="https://meet.google.com/abc-defg-hij"
                  value={meetingLink}
                  onChange={(e) => setMeetingLink(e.target.value)}
                  className="pl-9 bg-white"
                />
              </div>
              {meetingLink && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleCopyMeetingLink}
                  title="Copier le lien"
                  data-testid="meeting-link-copy"
                  className="bg-white"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="meeting-link-save"
                onClick={() => handleSaveMeetingLink(false)}
                disabled={isSavingLink}
                className="bg-white"
              >
                <Save className="w-4 h-4 mr-2" />
                Enregistrer
              </Button>
              <Button
                type="button"
                size="sm"
                data-testid="meeting-link-send"
                onClick={() => handleSaveMeetingLink(true)}
                disabled={isSavingLink || !meetingLink.trim()}
              >
                <Send className="w-4 h-4 mr-2" />
                Envoyer au client
              </Button>
              {meetingLink && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => window.open(meetingLink, '_blank', 'noopener,noreferrer')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Ouvrir
                </Button>
              )}
            </div>
            {request.meeting_link && (
              <p className="text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2 break-all">
                ✓ Lien actuel : <span className="font-mono">{request.meeting_link}</span>
              </p>
            )}
          </div>
        </Section>
      )}

      {/* Section Affectation collaborateur */}
      {(type === 'punctual' || type === 'appointment' || type === 'subscription') && (
        <Section
          title={hasAssignedCollab ? 'Collaborateur affecté' : (hasPreferredOnly ? 'Choix du client' : 'Affectation collaborateur')}
          icon={UserPlus}
          tone={hasAssignedCollab ? 'default' : (hasPreferredOnly ? 'purple' : 'blue')}
        >
          {hasAssignedCollab ? (() => {
            const c = resolveCollab(assignedCollabId, request.assigned_collaborator_name || request.collaborator_name);
            return (
              <div className="flex items-center gap-3">
                {c?.avatar_url ? (
                  <img
                    src={c.avatar_url}
                    alt={c.name}
                    className="w-10 h-10 rounded-xl object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                    data-testid="assigned-collab-avatar"
                  />
                ) : (
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center font-semibold text-white"
                    style={{ backgroundColor: c?.color || '#0f766e' }}
                  >
                    {(c?.name || '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate" data-testid="assigned-collab-name">
                    {c?.name || 'Collaborateur affecté'}
                  </p>
                  <Badge variant="secondary" className="mt-0.5 bg-emerald-100 text-emerald-800 text-[10px]">
                    Affecté
                  </Badge>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-white"
                  onClick={loadSuggestions}
                  data-testid="reassign-collaborator-btn"
                >
                  <Shuffle className="w-4 h-4 mr-1.5" />
                  Réaffecter
                </Button>
              </div>
            );
          })() : hasPreferredOnly ? (() => {
            const c = resolveCollab(preferredCollabId, request.preferred_collaborator_name);
            return (
              <div className="space-y-3">
                <div className="flex items-center gap-3 bg-white border border-purple-200 rounded-xl p-3">
                  {c?.avatar_url ? (
                    <img
                      src={c.avatar_url}
                      alt={c.name}
                      className="w-10 h-10 rounded-xl object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                      data-testid="preferred-collab-avatar"
                    />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center font-semibold text-white"
                      style={{ backgroundColor: c?.color || '#7c3aed' }}
                    >
                      {(c?.name || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate" data-testid="preferred-collab-name">
                      {c?.name || 'Collaborateur demandé'}
                    </p>
                    <Badge variant="secondary" className="mt-0.5 bg-purple-100 text-purple-800 text-[10px]">
                      Préférence client
                    </Badge>
                  </div>
                </div>
                <p className="text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
                  Le client a demandé cet intervenant. Confirmez l'affectation ci-dessous ou choisissez-en un autre selon les disponibilités.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={() => handleAssign(preferredCollabId)}
                    disabled={isAssigning}
                    className="flex-1"
                    data-testid="confirm-preferred-collab-btn"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    {isAssigning ? 'Affectation…' : `Confirmer ${c?.name || 'ce choix'}`}
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 bg-white"
                    onClick={loadSuggestions}
                    data-testid="change-preferred-collab-btn"
                  >
                    <Shuffle className="w-4 h-4 mr-2" />
                    Voir les alternatives
                  </Button>
                </div>
              </div>
            );
          })() : (
            <div className="space-y-3">
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3">
                <Sparkles className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-900">Le client a coché « Peu importe »</p>
                  <p className="text-xs text-amber-700">
                    Vous devez affecter un collaborateur. Nous vous suggérons les plus disponibles selon le planning du jour.
                  </p>
                </div>
              </div>
              <Button
                onClick={loadSuggestions}
                data-testid="assign-collaborator-btn"
                className="w-full"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Affecter un collaborateur
              </Button>
            </div>
          )}
        </Section>
      )}
      
      {/* Sections spécifiques abonnement (grid 2 colonnes) */}
      {type === 'subscription' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {(request.start_date || request.end_date) && (
            <Section title="Période d'abonnement" icon={CalendarDays}>
              <InfoField icon={Calendar} label="Début" value={formatDate(request.start_date)} />
              <InfoField icon={Calendar} label="Fin" value={formatDate(request.end_date)} />
            </Section>
          )}

          {(request.preferred_day || request.preferred_period) && (
            <Section title="Préférences client" icon={Clock}>
              <InfoField
                icon={Calendar}
                label="Jour préféré"
                value={DAY_LABELS[request.preferred_day] || request.preferred_day}
              />
              <InfoField
                icon={Clock}
                label="Période préférée"
                value={PERIOD_LABELS[request.preferred_period] || request.preferred_period}
              />
            </Section>
          )}

          {(request.estimated_monthly > 0 || request.price_per_hour) && (
            <Section title="Tarification" icon={Euro} tone="primary">
              <InfoField
                icon={Euro}
                label="Tarif horaire"
                value={`${request.price_per_hour}€/h`}
              />
              {request.estimated_monthly > 0 && (
                <InfoField
                  icon={Euro}
                  label="Mensualité estimée"
                  value={`${request.estimated_monthly.toFixed(2)}€`}
                />
              )}
              {request.estimated_monthly_urssaf > 0 && (
                <InfoField
                  icon={Euro}
                  label="Après crédit URSSAF (50%)"
                  value={`${request.estimated_monthly_urssaf.toFixed(2)}€`}
                />
              )}
              {request.estimated_annual > 0 && (
                <InfoField
                  icon={Euro}
                  label="Total annuel estimé"
                  value={`${request.estimated_annual.toFixed(2)}€`}
                />
              )}
            </Section>
          )}
        </div>
      )}

      {/* Section Prix - pour les réservations ponctuelles */}
      {(type === 'punctual' || type === 'appointment') && request.total_price > 0 && (
        <Section title="Tarification" icon={Euro} tone="primary">
          <InfoField
            icon={Euro}
            label="Prix total"
            value={`${request.total_price.toFixed(2)}€`}
          />
        </Section>
      )}
      
      {/* Section Dates sélectionnées - pour les abonnements */}
      {type === 'subscription' && (request.selected_dates?.length > 0 || request.selected_slots?.length > 0) && (
        <Section title="Dates sélectionnées par le client">
          <div className="space-y-2">
            {(request.selected_dates || request.selected_slots || []).map((dateEntry, idx) => {
              const dateStr = typeof dateEntry === 'string' ? dateEntry : dateEntry?.date;
              const period = typeof dateEntry === 'object' ? dateEntry?.period : null;
              if (!dateStr) return null;
              
              try {
                const formattedDate = new Date(dateStr).toLocaleDateString('fr-FR', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short'
                });
                return (
                  <div key={idx} className="flex items-center justify-between bg-amber-50 px-3 py-2 rounded-md border border-amber-200">
                    <div className="flex items-center gap-2">
                      <CalendarDays className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-medium">{formattedDate}</span>
                      {period && (
                        <Badge variant="outline" className="text-xs">
                          {period === 'morning' ? 'Matin' : 'Après-midi'}
                        </Badge>
                      )}
                    </div>
                    <Badge className="bg-amber-100 text-amber-800 text-xs">En attente</Badge>
                  </div>
                );
              } catch {
                return null;
              }
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Ces dates seront créées comme réservations en attente de validation après approbation de l'abonnement.
          </p>
        </Section>
      )}
      
      {/* Section Dates proposées par l'entreprise */}
      {type === 'subscription' && request.proposed_dates_by_company?.length > 0 && (
        <Section title="Dates proposées par l'entreprise">
          <div className="space-y-2">
            {request.proposed_dates_by_company.map((dateStr, idx) => {
              try {
                const formattedDate = new Date(dateStr).toLocaleDateString('fr-FR', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short'
                });
                return (
                  <div key={idx} className="flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-md border border-blue-200">
                    <CalendarDays className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium">{formattedDate}</span>
                  </div>
                );
              } catch {
                return null;
              }
            })}
          </div>
          {request.status === 'awaiting_client_validation' && (
            <p className="text-xs text-blue-600 mt-2">
              En attente de validation par le client.
            </p>
          )}
        </Section>
      )}
      
      {/* Réponses aux questions personnalisées */}
      <CustomAnswers answers={request.custom_answers} questions={questions} />
      
      {/* Bouton pour accéder à la gestion du planning de l'abonnement */}
      {type === 'subscription' && request.status === 'approved' && request.subscription_id && (
        <div className="pt-4 border-t">
          <Button 
            className="w-full"
            onClick={() => navigate(`/company/subscriptions/${request.subscription_id}`)}
          >
            <CalendarDays className="w-4 h-4 mr-2" />
            Gérer le planning des passages
          </Button>
        </div>
      )}
      
      {/* Notes */}
      {(request.notes || request.needs_description) && (
        <Section title="Notes et commentaires">
          <InfoField icon={FileText} label="Notes" value={request.notes} />
          <InfoField icon={FileText} label="Description du besoin" value={request.needs_description} />
        </Section>
      )}
      
      {/* Section Métadonnées */}
      <Section title="Informations système" icon={FileText}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <InfoField
            icon={Calendar}
            label="Créé le"
            value={formatDateTime(request.created_at)}
          />
          {request.confirmed_at && (
            <InfoField
              icon={Calendar}
              label="Confirmé le"
              value={formatDateTime(request.confirmed_at)}
            />
          )}
          {request.rejection_reason && (
            <InfoField
              icon={FileText}
              label="Motif de refus"
              value={request.rejection_reason}
            />
          )}
        </div>
      </Section>
    </div>
  );
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] p-0 overflow-hidden gap-0 rounded-3xl">
        {/* Modern header with client identity on the left */}
        <DialogHeader className={`px-8 pt-7 pb-6 border-b relative overflow-hidden ${
          type === 'subscription' ? 'bg-gradient-to-br from-purple-50 via-white to-white' :
          type === 'appointment' ? 'bg-gradient-to-br from-blue-50 via-white to-white' :
          'bg-gradient-to-br from-emerald-50 via-white to-white'
        }`}>
          {/* Decorative accent stripe */}
          <div className={`absolute top-0 left-0 right-0 h-1 ${
            type === 'subscription' ? 'bg-gradient-to-r from-purple-400 via-purple-500 to-purple-600' :
            type === 'appointment' ? 'bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600' :
            'bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600'
          }`} />
          <DialogTitle className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4 min-w-0">
              {/* Client avatar as identity anchor */}
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-md flex-shrink-0 font-bold text-lg ${
                type === 'subscription' ? 'bg-gradient-to-br from-purple-500 to-purple-600' :
                type === 'appointment' ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                'bg-gradient-to-br from-emerald-500 to-emerald-600'
              }`}>
                {(request.client_name || '?').charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 pt-0.5 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                  <span className="font-mono text-[11px] bg-white text-gray-700 px-2.5 py-1 rounded-md font-semibold flex items-center gap-1 border border-gray-200">
                    <Hash className="w-3 h-3 text-gray-400" />
                    {request.request_number || `${request.id?.substring(0, 8)}`}
                  </span>
                  <Badge className={`${status.color} border-0 font-semibold`}>{status.label}</Badge>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md ${
                    type === 'subscription' ? 'bg-purple-100 text-purple-700' :
                    type === 'appointment' ? 'bg-blue-100 text-blue-700' :
                    'bg-emerald-100 text-emerald-700'
                  }`}>
                    {type === 'subscription' ? (<><Repeat className="w-2.5 h-2.5 inline mr-0.5" />Abonnement</>) :
                      type === 'appointment' ? (<><CalendarDays className="w-2.5 h-2.5 inline mr-0.5" />Rendez-vous</>) :
                      (<><ClipboardCheck className="w-2.5 h-2.5 inline mr-0.5" />Ponctuelle</>)}
                  </span>
                </div>
                <p className="text-xl font-bold text-gray-900 leading-tight truncate" data-testid="modal-client-name-header">
                  {request.client_name || 'Client'}
                </p>
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                  {request.client_email}{request.client_phone ? ` • ${request.client_phone}` : ''}
                </p>
              </div>
            </div>
            {!isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(true)}
                data-testid="modal-edit-btn"
                className="bg-white shadow-sm shrink-0 hover:shadow-md transition-shadow"
              >
                <Edit className="w-4 h-4 mr-1.5" />
                Modifier
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Scrollable body */}
        <div className="px-6 sm:px-8 py-6 overflow-y-auto bg-gray-50/30" style={{ maxHeight: 'calc(92vh - 200px)' }}>
          {isEditing ? renderEditForm() : renderViewMode()}
        </div>

        {/* Sticky footer for edit mode */}
        {isEditing && (
          <DialogFooter className="px-8 py-5 border-t bg-gray-50/70 gap-3">
            <Button
              variant="outline"
              onClick={() => setIsEditing(false)}
              disabled={isSaving}
              className="bg-white"
            >
              <X className="w-4 h-4 mr-1" />
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              data-testid="modal-save-btn"
            >
              <Save className="w-4 h-4 mr-1" />
              {isSaving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        )}

        {/* Sticky footer for view mode — quick approve / reject */}
        {!isEditing && canApproveOrReject && (
          <DialogFooter className="px-8 py-4 border-t bg-white gap-3 shadow-[0_-4px_20px_-8px_rgba(0,0,0,0.08)]">
            <Button
              variant="outline"
              onClick={handleReject}
              disabled={isActing}
              className="bg-white text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-300 font-semibold transition-all"
              data-testid="modal-reject-btn"
            >
              <XCircle className="w-4 h-4 mr-1.5" />
              Refuser
            </Button>
            <Button
              onClick={handleApprove}
              disabled={isActing}
              className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold shadow-md hover:shadow-lg transition-all"
              data-testid="modal-approve-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              {isActing ? 'En cours…' : 'Approuver'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>

      {/* Sub-dialog: pick collaborator(s) with multi-intervenants support */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-md">
          <MultiCollabAssignDialog
            key={request?.id + '-' + showAssignDialog}
            requiredCount={requiredCollabCount}
            suggestedCollabs={suggestedCollabs}
            preferredCollabId={preferredCollabId}
            isAssigning={isAssigning}
            onCancel={() => setShowAssignDialog(false)}
            onConfirm={(ids) => handleAssign(ids)}
          />
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

// -------- Multi-collab assignment dialog body --------
// Split into its own component so the internal selection state resets each
// time the dialog is re-opened (keyed by request id).
function MultiCollabAssignDialog({
  requiredCount,
  suggestedCollabs,
  preferredCollabId,
  isAssigning,
  onCancel,
  onConfirm,
}) {
  const [selectedIds, setSelectedIds] = useState(() => {
    // Pre-select the client's preferred collab if it is available.
    if (preferredCollabId) {
      const pref = suggestedCollabs.find(c => c.id === preferredCollabId);
      if (pref && pref.is_available !== false) return [preferredCollabId];
    }
    return [];
  });

  const toggle = (id, isAvailable) => {
    if (!isAvailable) return;
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= requiredCount) {
        // If we already have the required number, drop the oldest to make room
        return [...prev.slice(1), id];
      }
      return [...prev, id];
    });
  };

  const canConfirm = selectedIds.length === requiredCount && !isAssigning;
  const availableCount = suggestedCollabs.filter(c => c.is_available !== false).length;
  const notEnoughAvailable = availableCount < requiredCount;

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-primary" />
          {requiredCount > 1 ? `Affecter ${requiredCount} intervenants` : 'Affecter un collaborateur'}
        </DialogTitle>
      </DialogHeader>

      {/* Travel map — only shown when we have any coord data (destination) */}
      {suggestedCollabs.some(c => c.origin_coords) && suggestedCollabs[0]?.dest_coords && (
        <div className="pb-2">
          <TravelMap
            clientCoords={suggestedCollabs[0].dest_coords}
            collabs={suggestedCollabs}
            height={200}
          />
        </div>
      )}

      {/* Progress hint */}
      <div className="flex items-center justify-between text-xs text-gray-600 pb-2">
        <span data-testid="assign-selection-count">
          {selectedIds.length}/{requiredCount} sélectionné{requiredCount > 1 ? 's' : ''}
        </span>
        <span>{availableCount} dispo sur {suggestedCollabs.length} pour ce créneau</span>
      </div>

      {notEnoughAvailable && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 mb-2" data-testid="not-enough-collabs-warning">
          Il n'y a que {availableCount} collaborateur{availableCount > 1 ? 's' : ''} disponible{availableCount > 1 ? 's' : ''} sur ce créneau, mais la prestation en requiert {requiredCount}. Choisissez une autre date ou libérez un collaborateur.
        </div>
      )}

      <div className="space-y-2 max-h-[55vh] overflow-y-auto">
        {suggestedCollabs.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            Aucun collaborateur éligible pour cette demande.
          </p>
        ) : (
          <>
            {suggestedCollabs[0]?.fallback && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 mb-2" data-testid="collab-fallback-warning">
                Aucun collaborateur ne correspond aux filtres (catégorie / widget / service).
                Voici tous les collaborateurs actifs.
              </div>
            )}
            {suggestedCollabs.map((c, idx) => {
              const isAvailable = c.is_available !== false;
              const isSelected = selectedIds.includes(c.id);
              const isPreferred = preferredCollabId && c.id === preferredCollabId;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggle(c.id, isAvailable)}
                  disabled={!isAvailable || isAssigning}
                  data-testid={`suggest-collab-${c.id}${isAvailable ? '' : '-busy'}`}
                  aria-pressed={isSelected}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors text-left ${
                    isSelected ? 'border-primary bg-primary/5 ring-2 ring-primary/20' :
                    isAvailable ? 'border-gray-200 hover:border-primary hover:bg-primary/5' :
                    'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                  }`}
                >
                  {/* Avatar */}
                  {c.avatar_url ? (
                    <img
                      src={c.avatar_url}
                      alt={c.name}
                      className={`w-10 h-10 rounded-xl object-cover ${isAvailable ? '' : 'grayscale'}`}
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center font-semibold text-white ${c.avatar_url ? 'hidden' : ''} ${isAvailable ? '' : 'grayscale'}`}
                    style={{ backgroundColor: c.color || (idx === 0 ? '#059669' : '#6b7280') }}
                  >
                    {(c.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`font-semibold text-sm truncate ${!isAvailable ? 'line-through text-gray-500' : ''}`}>{c.name}</span>
                      {isPreferred && (
                        <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-[10px]">
                          Choix client
                        </Badge>
                      )}
                      {idx === 0 && isAvailable && !isPreferred && (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">
                          <Sparkles className="w-2.5 h-2.5 mr-0.5" />
                          Suggéré
                        </Badge>
                      )}
                      {!isAvailable && (
                        <Badge className="bg-gray-200 text-gray-600 text-[10px]">Occupé</Badge>
                      )}
                      {c.role === 'commercial' && (
                        <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px]">
                          Commercial
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {c.load_on_date === 0 ? 'Aucun RDV ce jour-là' :
                        c.load_on_date === 1 ? '1 RDV ce jour-là' :
                        `${c.load_on_date} RDV ce jour-là`}
                      {c.travel_minutes != null && (
                        <span className="ml-2 inline-flex items-center gap-1 text-emerald-700" data-testid={`collab-travel-${c.id}`}>
                          · <Car className="w-3 h-3" />
                          {c.travel_minutes < 60
                            ? `${c.travel_minutes} min`
                            : `${Math.floor(c.travel_minutes / 60)}h${String(c.travel_minutes % 60).padStart(2, '0')}`}
                          {c.travel_km != null && (<span className="text-gray-400"> · {c.travel_km} km</span>)}
                        </span>
                      )}
                    </p>
                  </div>
                  {/* Selection state */}
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'bg-primary border-primary' : 'border-gray-300'
                  }`}>
                    {isSelected && <CheckCircle2 className="w-3 h-3 text-white" strokeWidth={3} />}
                  </div>
                </button>
              );
            })}
          </>
        )}
      </div>

      <DialogFooter className="pt-4 gap-2">
        <Button variant="outline" onClick={onCancel} disabled={isAssigning}>
          Annuler
        </Button>
        <Button
          onClick={() => onConfirm(requiredCount > 1 ? selectedIds : selectedIds[0])}
          disabled={!canConfirm}
          data-testid="confirm-multi-assign-btn"
          className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold"
        >
          {isAssigning ? 'Affectation…' : (
            requiredCount > 1
              ? `Valider les ${requiredCount} intervenants`
              : 'Valider l\'affectation'
          )}
        </Button>
      </DialogFooter>
    </>
  );
}
